import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = articles
    .filter((a) => a.slug !== slug && a.category === article?.category)
    .slice(0, 3);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-32 pb-16 text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-2xl">
            <i className="ri-file-unknow-line text-3xl text-foreground-300"></i>
          </div>
          <h1 className="font-heading text-2xl text-foreground-950 mb-2">文章未找到</h1>
          <p className="text-foreground-500 mb-6">该文章可能已被移除或链接地址有误</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '狼友域名',
    },
    publisher: {
      '@type': 'Organization',
      name: '狼友域名',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://langyouyuming.com/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://langyouyuming.com/' },
      { '@type': 'ListItem', position: 2, name: '文章资讯', item: 'https://langyouyuming.com/article/how-to-access-langyou' },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: '文章资讯', to: '/article/how-to-access-langyou' },
                  { label: article.title },
                ]}
              />
            </div>

            {/* Article Header */}
            <article>
              <header className="mb-8">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-xs bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full whitespace-nowrap">
                    {article.categoryName}
                  </span>
                  <span className="text-xs text-foreground-400">{article.date}</span>
                  <span className="text-xs text-foreground-400">
                    <i className="ri-time-line mr-1"></i>{article.readTime}
                  </span>
                  <span className="text-xs text-foreground-400">
                    <i className="ri-eye-line mr-1"></i>{article.viewCount.toLocaleString()} 阅读
                  </span>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 leading-snug mb-3">
                  {article.title}
                </h1>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                  {article.excerpt}
                </p>
              </header>

              {/* Tags */}
              <div className="flex flex-wrap items-center gap-2 mb-8">
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full whitespace-nowrap">
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Article Content */}
              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                  [&_h2]:font-heading [&_h2]:text-xl [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
                  [&_p]:mb-4 [&_p]:leading-relaxed
                  [&_strong]:text-foreground-900"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Share */}
              <div className="mt-10 pt-6 border-t border-background-200/70">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-foreground-500">分享到：</span>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer" aria-label="复制链接">
                    <i className="ri-link text-sm"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer" aria-label="分享到微信">
                    <i className="ri-wechat-line text-sm"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer" aria-label="分享到微博">
                    <i className="ri-weibo-line text-sm"></i>
                  </button>
                </div>
              </div>
            </article>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="mt-12">
                <h2 className="font-heading text-xl text-foreground-950 mb-6">相关文章</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300 transition-all duration-300 p-5 cursor-pointer"
                    >
                      <span className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                        {ra.categoryName}
                      </span>
                      <h3 className="font-heading text-base text-foreground-950 mt-3 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="text-sm text-foreground-500 line-clamp-2">{ra.excerpt}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}